import { z } from "zod";
/**
 * The brand identity boundary.
 *
 * Adding a brand is the one write in the product that can silently corrupt
 * every number downstream, because two rows for one company split its share of
 * voice between them and nothing anywhere says so. So this boundary does not
 * have a "create brand" response — it has a *resolution*, and one of its
 * outcomes is "we cannot tell, a person decides" (AGENTS.md §1).
 */
/** A domain as a person types it: with or without scheme, path, or www. */
export const websiteSchema = z.string().trim().min(3).max(2048);
export const brandSchema = z.object({
    id: z.string().min(1),
    name: z.string().min(1),
    /** As entered, for display. The comparison key is in `domains`. */
    primaryDomain: z.string().min(1),
    /** Other names this brand answers to, as configured. */
    aliases: z.array(z.string()),
    /**
     * Every site this brand owns, already reduced to its registrable domain.
     * This is what resolution compares, so it is shown rather than hidden — a
     * customer who cannot see the key cannot tell why a suggestion was refused.
     */
    domains: z.array(z.string()),
    /**
     * The market this brand is measured in, and the language it is asked about.
     *
     * On the brand, because that is where it is true. An engine answers
     * differently by market and by language, so a capture needs both — and a
     * question does not have a market of its own. Two brands here share a capture
     * of a shared question; two brands in different markets each get their own,
     * because those are two different answers.
     */
    country: z.string().length(2),
    language: z.string().min(2),
});
export const brandListSchema = z.object({ brands: z.array(brandSchema) });
export const createBrandSchema = z.object({
    name: z.string().trim().min(1).max(120),
    website: websiteSchema,
    /**
     * Empty by default and deliberately so. A wrong alias inflates every coverage
     * number that uses it, and the display name and domain are already searched
     * without being listed here.
     */
    aliases: z.array(z.string().trim().min(1).max(120)).max(20).default([]),
    /**
     * The market to measure this brand in.
     *
     * Defaulted rather than required, so adding a competitor stays a two-field
     * form: a competitor is measured alongside the subject, in the subject's own
     * market, and asking again for each one would be asking a question with one
     * answer. The subject's own market comes from onboarding, which asks.
     */
    country: z
        .string()
        .trim()
        .length(2)
        .transform((value) => value.toUpperCase())
        .default("IN"),
    language: z.string().trim().min(2).max(12).default("en"),
});
/**
 * Editing a brand already being measured.
 *
 * Every field optional, and only what is sent changes. A full replacement would
 * make a form that shows three fields silently blank a fourth it never asked
 * about — and the fourth here is `aliases`, where an accidental empty array
 * quietly stops counting half a brand's mentions.
 *
 * **Renaming and re-domaining are not cosmetic.** The name and every alias are
 * what mention counting matches on, and the domains are what citation
 * attribution matches on. Changing either changes what future captures count —
 * never what past ones counted, because those numbers are records of answers
 * that have already been read. A brand renamed today does not retroactively
 * gain the mentions it missed last week, and pretending otherwise would be
 * inventing a measurement.
 */
export const updateBrandSchema = z.object({
    name: z.string().trim().min(1).max(120).optional(),
    /** The main site, as a person types it. Re-keyed on the way in. */
    website: websiteSchema.optional(),
    aliases: z.array(z.string().trim().min(1).max(120)).max(20).optional(),
    /**
     * Every other site this brand owns.
     *
     * The primary is not repeated here — it is `website`, and holding it in two
     * places is how the two drift. Sent whole rather than as additions and
     * removals, because this is the one field a form can present completely: a
     * reader sees the entire list and edits it.
     */
    otherDomains: z.array(websiteSchema).max(20).optional(),
    country: z
        .string()
        .trim()
        .length(2)
        .transform((value) => value.toUpperCase())
        .optional(),
    language: z.string().trim().min(2).max(12).optional(),
});
/**
 * What happened when we tried to add a brand.
 *
 * `existing` is not an error and not a silent no-op: it is the answer that
 * makes "run Suggest Competitors twice" safe, and the caller shows it as
 * *already tracked* rather than pretending something was added.
 */
export const brandResolutionSchema = z.discriminatedUnion("outcome", [
    z.object({ outcome: z.literal("created"), brand: brandSchema }),
    z.object({ outcome: z.literal("existing"), brand: brandSchema }),
    /**
     * The name matches something we hold and the site does not. Both readings —
     * one company with two domains, or two companies sharing a name — are
     * ordinary, so we hand back the candidates and write nothing.
     */
    z.object({
        outcome: z.literal("ambiguous"),
        domainKey: z.string().min(1),
        candidates: z.array(brandSchema),
    }),
]);
//# sourceMappingURL=brands.js.map